/**
 * Disponibilidade: de regra semanal para horario concreto.
 *
 * ── Por que aqui, e nao em SQL ──
 * A tela precisa da MESMA resposta que o servidor: ela desenha a grade e diz
 * "livre", ele aceita ou recusa o agendamento. Duas implementacoes divergem no
 * primeiro caso de borda — e o primeiro caso de borda aqui e semana do mes.
 *
 * ── Tudo em minutos desde a meia-noite, e data como 'AAAA-MM-DD' ──
 * Fuso horario NAO entra neste arquivo. Um `Date` de JavaScript carrega
 * instante e fuso juntos, e "10h de segunda no consultorio" nao e um instante:
 * e uma hora local que vira instante so quando se sabe o fuso do local. Manter
 * isso fora daqui e o que torna as regras testaveis sem enganar.
 */
export function minutosDe(hhmm) {
    const [h = '0', m = '0'] = hhmm.split(':');
    return Number(h) * 60 + Number(m);
}
export function paraHhmm(minutos) {
    const h = Math.floor(minutos / 60);
    return `${String(h).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}`;
}
/** Sem `new Date`: 'AAAA-MM-DD' e texto ordenavel, e comparar texto nao tem fuso. */
function diaDaSemana(data) {
    const [a = '1970', m = '01', d = '01'] = data.split('-');
    return new Date(Date.UTC(Number(a), Number(m) - 1, Number(d))).getUTCDay();
}
/**
 * A quantas semanas do mes o dia pertence — 1 para os dias 1 a 7, 2 para 8 a 14.
 *
 * Contagem por bloco de sete, e nao "quantas segundas ja houve": e assim que
 * uma pessoa le "primeira segunda do mes" olhando o calendario, e a diferenca
 * entre os dois aparece justamente nos meses que comecam no fim de semana.
 */
export function semanaDoMes(data) {
    const [, , d = '01'] = data.split('-');
    return Math.floor((Number(d) - 1) / 7) + 1;
}
function valeNaData(r, data) {
    if (!r.active)
        return false;
    if (r.weekday !== diaDaSemana(data))
        return false;
    if (data < r.validFrom)
        return false;
    if (r.validUntil !== null && data > r.validUntil)
        return false;
    if (r.weeksOfMonth.length > 0 && !r.weeksOfMonth.includes(semanaDoMes(data)))
        return false;
    return true;
}
/**
 * Corta de um intervalo os pedacos cobertos por outros.
 *
 * Um corte no meio parte o intervalo em DOIS — manha e tarde, com o congresso no
 * meio. Devolver so a primeira metade e o bug classico deste calculo.
 *
 * Generico em `T` para preservar o que o chamador carrega junto: uma faixa cortada
 * continua sabendo o local dela.
 */
export function subtrairIntervalos(alvos, cortes) {
    let pedacos = [...alvos];
    for (const c of cortes) {
        const proximos = [];
        for (const p of pedacos) {
            if (c.fim <= p.inicio || c.inicio >= p.fim) {
                proximos.push(p);
                continue;
            }
            if (c.inicio > p.inicio)
                proximos.push({ ...p, fim: c.inicio });
            if (c.fim < p.fim)
                proximos.push({ ...p, inicio: c.fim });
        }
        pedacos = proximos;
    }
    return pedacos;
}
/**
 * Junta intervalos que se tocam ou se sobrepoem, em ordem.
 *
 * Dois intervalos sobrepostos desenhados um sobre o outro sao verde sobre verde na
 * tela, e a conta de minutos conta o mesmo minuto duas vezes.
 */
export function unirIntervalos(intervalos) {
    const validos = intervalos.filter((x) => x.fim > x.inicio);
    if (validos.length === 0)
        return [];
    const ordenados = [...validos].sort((a, b) => a.inicio - b.inicio);
    const saida = [];
    let atual = ordenados[0];
    for (const x of ordenados.slice(1)) {
        if (x.inicio <= atual.fim)
            atual = { ...atual, fim: Math.max(atual.fim, x.fim) };
        else {
            saida.push(atual);
            atual = x;
        }
    }
    saida.push(atual);
    return saida;
}
function ondeAtende(f) {
    return `${f.locationId ?? 'tele'}|${f.modality}`;
}
/**
 * Junta faixas do mesmo local que se tocam ou se sobrepoem.
 *
 * "seg 09h-18h" mais "seg 10h-13h30" no mesmo consultorio e configuracao
 * legitima, e sem unir viram duas janelas desenhadas uma sobre a outra.
 */
function unir(faixas) {
    const porLocal = new Map();
    for (const f of faixas) {
        const chave = ondeAtende(f);
        const lista = porLocal.get(chave);
        if (lista === undefined)
            porLocal.set(chave, [f]);
        else
            lista.push(f);
    }
    return [...porLocal.values()]
        .flatMap((lista) => unirIntervalos(lista))
        .sort((a, b) => a.inicio - b.inicio);
}
/**
 * As janelas de atendimento de um dia, ja com as excecoes aplicadas.
 *
 * Ordem importa: o `extra` entra DEPOIS do `block`. Abrir um sabado num mes de
 * ferias tem que funcionar — e funciona porque o bloqueio corta o padrao
 * semanal, nao o que foi aberto de proposito.
 *
 * A saida nunca tem duas janelas sobrepostas no mesmo local. Quem desenha a
 * agenda depende disso: janela sobreposta viraria verde sobre verde, e a conta
 * de minutos livres do dia contaria o mesmo minuto duas vezes.
 */
export function faixasDoDia(data, regras, excecoes) {
    const doDia = excecoes.filter((e) => e.onDate === data);
    const cortes = doDia
        .filter((e) => e.kind === 'block')
        .map((e) => ({
        inicio: e.startsAt === null ? 0 : minutosDe(e.startsAt),
        fim: e.endsAt === null ? 24 * 60 : minutosDe(e.endsAt),
    }));
    const doPadrao = unir(regras
        .filter((r) => valeNaData(r, data))
        .map((r) => ({
        inicio: minutosDe(r.startsAt),
        fim: minutosDe(r.endsAt),
        locationId: r.locationId,
        modality: r.modality,
        extra: false,
    }))
        .flatMap((f) => subtrairIntervalos([f], cortes)));
    const extras = unir(doDia
        .filter((e) => e.kind === 'extra' && e.startsAt !== null && e.endsAt !== null)
        .map((e) => ({
        inicio: minutosDe(e.startsAt),
        fim: minutosDe(e.endsAt),
        locationId: e.locationId,
        modality: e.modality ?? 'in_person',
        extra: true,
    })));
    // O extra guarda so o que a rotina ainda nao cobria. Se a segunda ja abria
    // 09h-18h, um extra de 10h-12h nao abriu nada, e pintar aquele pedaco como
    // extra atribuiria uma decisao que ninguem tomou.
    const abertoDeProposito = extras.flatMap((e) => subtrairIntervalos([e], doPadrao.filter((p) => ondeAtende(p) === ondeAtende(e))));
    return [...doPadrao, ...abertoDeProposito].sort((a, b) => a.inicio - b.inicio);
}
/**
 * Onde cabe uma consulta desta duracao.
 *
 * Sugestao, e nao definicao de disponibilidade: quem define e a janela. Serve
 * para a atendente que quer "40 min na terca de manha" e para publicar horarios
 * numa plataforma de terceiro, que exige lista discreta.
 *
 * O ultimo comeco so entra se a consulta INTEIRA cabe na janela: 10h as 11h com
 * consulta de 40 min tem um comeco, e nao dois — o segundo terminaria 11h20.
 */
export function sugestoesPara(duracaoMin, data, regras, excecoes) {
    const saida = [];
    for (const f of faixasDoDia(data, regras, excecoes)) {
        for (let m = f.inicio; m + duracaoMin <= f.fim; m += duracaoMin) {
            saida.push({
                hora: paraHhmm(m),
                minutos: m,
                duracao: duracaoMin,
                locationId: f.locationId,
                modality: f.modality,
                extra: f.extra,
            });
        }
    }
    return saida.sort((a, b) => a.minutos - b.minutos);
}
/**
 * A consulta cai na janela de atendimento?
 *
 * ── Isto NAO e permissao ──
 * A resposta serve para colorir a agenda e para avisar a atendente antes de
 * confirmar. Marcar fora da janela e legitimo e acontece todo dia: o retorno
 * rapido antes de abrir, o paciente que so consegue as 19h. Transformar esta
 * funcao em barreira e o erro que ela existe para evitar — quem barra e a
 * `EXCLUDE` de `appointments`, e o que ela barra e duas pessoas no mesmo
 * intervalo, que e outra pergunta. Ver ADR 0020.
 *
 * Os dois motivos ficam separados porque a saida difere: fora da janela nao tem
 * conserto (e a decisao de quem marca), e estourar a janela se conserta
 * encurtando a consulta.
 */
export function classificarNaJanela(data, inicioMin, duracaoMin, regras, excecoes, 
/**
 * Onde a consulta seria. Ausente = qualquer local serve, e a resposta e a
 * melhor das janelas do dia — e o que a conversa consegue perguntar, porque ali
 * a paciente pede um horario e nao um consultorio.
 */
alvo) {
    const candidatas = faixasDoDia(data, regras, excecoes).filter((f) => inicioMin >= f.inicio &&
        inicioMin < f.fim &&
        (alvo === undefined || (f.locationId === alvo.locationId && f.modality === alvo.modality)));
    if (candidatas.length === 0)
        return 'fora_da_janela';
    return candidatas.some((f) => inicioMin + duracaoMin <= f.fim) ? 'dentro' : 'estoura_a_janela';
}
/**
 * O medico consegue estar la?
 *
 * Dois locais diferentes no mesmo dia exigem folga de deslocamento entre eles.
 * Sem isto a agenda aceita 11h40 no Centro e 12h00 na Barra, e alguem descobre
 * dirigindo.
 */
export function conflitaPorDeslocamento(horarios, travelBufferMin) {
    const ordenados = [...horarios].sort((a, b) => a.minutos - b.minutos);
    for (let i = 1; i < ordenados.length; i++) {
        const antes = ordenados[i - 1];
        const agora = ordenados[i];
        if (antes.locationId === agora.locationId)
            continue;
        // Teleconsulta nao tem deslocamento: a troca entre online e presencial
        // acontece na mesma cadeira.
        if (antes.locationId === null || agora.locationId === null)
            continue;
        if (agora.minutos - (antes.minutos + antes.duracao) < travelBufferMin)
            return true;
    }
    return false;
}
/** `manha` e antes do meio-dia; `tarde`, do meio-dia em diante. */
const MEIO_DIA = 12 * 60;
export function atendePreferencia(data, minutos, p) {
    if (p.weekdays.length > 0) {
        const [a = '1970', m = '01', d = '01'] = data.split('-');
        const dow = new Date(Date.UTC(Number(a), Number(m) - 1, Number(d))).getUTCDay();
        if (!p.weekdays.includes(dow))
            return false;
    }
    if (p.periodo === 'manha' && minutos >= MEIO_DIA)
        return false;
    if (p.periodo === 'tarde' && minutos < MEIO_DIA)
        return false;
    return true;
}
/**
 * As datas de uma serie: mesma cadencia, a partir da primeira.
 *
 * Oito sessoes semanais sao a primeira mais sete saltos de sete dias. Manter
 * isto puro e o que permite a tela MOSTRAR a serie inteira antes de gravar —
 * sem consultar o servidor para saber quais datas serao.
 *
 * Aritmetica em UTC de proposito: somar dias em horario local atravessa horario
 * de verao e produz a mesma hora em dias errados.
 */
export function datasDaSerie(primeira, quantidade, cadaSemanas = 1) {
    const saida = [];
    const base = Date.parse(`${primeira}T00:00:00Z`);
    if (Number.isNaN(base) || quantidade < 1)
        return saida;
    for (let i = 0; i < quantidade; i++) {
        saida.push(new Date(base + i * cadaSemanas * 7 * 86_400_000).toISOString().slice(0, 10));
    }
    return saida;
}
//# sourceMappingURL=agenda.js.map